import React, { useCallback, useMemo, useState } from 'react';

import { BugIcon } from '@primer/octicons-react';
import { StyledOcticon } from '@primer/react';

import CardView from '../../../common/CardView/CardView';
import ErrorSlate from '../../../ErrorSlate/ErrorSlate';
import LoadingSpinner from '../../../common/LoadingSpinner/LoadingSpinner';
import PaginationRow from '../../../common/PaginationRow/PaginationRow';
import SearchInput from '../../../common/TextInputs/SearchInput/SearchInput';

import FilterCountButton from '../../FilterCountButton';
import PipelineIcon from '../../../common/icons/PipelineIcon';

import PipelineTableFilterModal, { PIPELINE_STATUS as STATUS } from '../PipelineTableFilterModal/PipelineTableFilterModal';
import AdminPipelineHistoryModal from '../AdminPipelineHistoryModal/AdminPipelineHistoryModal';
import AdminPipelineRemovalModal from '../AdminPipelineRemovalModal/AdminPipelineRemovalModal';
import AdminPipelineVersionRemovalModal from '../AdminPipelineVersionRemovalModal/AdminPipelineVersionRemovalModal';
import PipelineAdminTable from '../PipelineAdminTable/PipelineAdminTable';
import PipelineDeploymentModal from '../PipelineDeploymentModal/PipelineDeploymentModal';

import useListPipelines from '../hooks/useListPipelines';
import useWorkspaceContext from '../../../workspaces/WorkspaceContext/WorkspaceContext';

import {
  deployPipelineWithoutModelConfigs,
  undeployPipeline,
} from '../../../../utils/pipelineDeployment';
import removePipeline from '../../../../utils/api_functions/removePipeline';
import removePipelineVersions from '../../../../utils/api_functions/removePipelineVersions';
import classNames from 'classnames';

const PIPELINE_ADMIN_MODAL = {
  DEPLOY_OR_UNDEPLOY: 'deploy_or_undeploy',
  MANAGE_VERSIONS: 'manage_versions',
  DELETE_PIPELINE: 'delete_pipeline',
  DELETE_SELECTED_VERSIONS: 'DELETE_SELECTED_VERSIONS',
  FILTER_PIPELINES: 'filter_pipelines',
  PIPELINE_HISTORY: 'pipeline_history',
}

const BugGraphic = () => (
  <StyledOcticon
    className="mb-2 text-brand"
    icon={BugIcon}
    size={64}
  />
)

const NoPipelinesGraphic = () => (
  <StyledOcticon
    className="mb-2 text-brand"
    icon={PipelineIcon}
    size={16}
  />
)

const countActivePipelineFilters = (filterObj = {}) => {
  let filterCount = 0;
  if (filterObj.status !== STATUS.All) {
    filterCount += 1;
  }
  if (filterObj.workspaces?.length) {
    filterCount += 1;
  }

  return filterCount;
}

export const PipelineAdminTab = ({
  error = null,
  loading = false,
  pipelines = [],
  workspaces = [],
  totalPipelineCount = 0,
  searchQuery = '',
  filterObj = { workspaces: [], status: STATUS.All },
  setSearchQuery = () => {},
  setFilterObj = () => {},
}) => {
  const [selectedPage, setSelectedPage] = useState(1);
  const [resultsPerPage, setResultsPerPage] = useState(25);

  const [showModal, setShowModal] = useState(null);
  // Either ID or Pipeline object as needed
  const [focusedPipeline, setFocusedPipeline] = useState(null)
  const [modalError, setModalError] = useState(null);

  const {
    setCurrentWorkspaceId,
  } = useWorkspaceContext();

  const handleSearchInputChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, [setSearchQuery])

  const handlePipelineRemoval = (pipelineId, workspaceId) => {
    setModalError(null);
    removePipeline(pipelineId, workspaceId).then((res) => {
      if (res?.code && res.code >= 400) {
        if (res.code === 409) {
          setModalError('Cannot delete a pipeline that is actively deployed. Please undeploy this pipeline before attempting to delete it.');
        } else {
          const message = res?.msg || 'An unexpected error has occurred while trying to remove the selected pipeline. Please try again in a few minutes.';
          setModalError(message);
        }
      } else {
        setShowModal(null);
        setFocusedPipeline(null);
        setModalError(null);
      }
    }).catch((e) => {
      if (e?.msg) {
        setModalError(e.msg);
      } else {
        setModalError('An unexpected error occurred while trying to remove the selected pipeline. Please try again in a few minutes.')
      }
    })
  }

  const handleMultiVersionRemoval = (versions) => {
    setModalError(null);
    removePipelineVersions(versions).then((res) => {
      if (res?.code && res.code >= 400) {
        if (res.code === 409) {
          setModalError('Cannot delete a pipeline that is actively deployed. Please undeploy this pipeline before attempting to delete it.');
        } else {
          const message = res?.msg || 'An unexpected error has occurred while trying to remove the selected pipeline version(s). Please try again in a few minutes.';
          setModalError(message);
        }
      } else {
        setShowModal(null);
        setFocusedPipeline(null);
        setModalError(null);
      }
    }).catch((e) => {
      if (e?.msg) {
        setModalError(e.msg);
      } else {
        setModalError('An unexpected error occurred while trying to remove the selected pipeline version(s). Please try again in a few minutes.')
      }
    })
  }

  const handleDeploy = (pipeline) => {
    const deploymentKey = pipeline?.deployment?.deploy_id;
    const pipelineID = pipeline?.id;
    const version = pipeline?.pipeline_version?.version;
    deployPipelineWithoutModelConfigs(deploymentKey, pipelineID, version).then((res) => {
      if (res?.code && res.code >= 400) {
        const message = res?.msg || 'An unexpected error has occurred while trying to deploy the selected pipeline. Please try again in a few minutes.';
        setModalError(message);
      } else {
        setShowModal(null);
        setFocusedPipeline(null);
        setModalError(null);
      }
    }).catch((e) => {
      // setActionLoading(false);
      if (e?.msg) {
        setModalError(e.msg);
      } else {
        setModalError('An unexpected error occurred while trying to deploy the selected pipeline. Please try again in a few minutes.')
      }
    })
  }

  const handleUndeploy = (pipeline) => {
    const pipeline_id = pipeline?.id;
    undeployPipeline({ pipeline_id }).then((res) => {
      if (res?.code && res.code >= 400) {
        const message = res?.msg || 'An unexpected error has occurred while trying to undeploy the selected pipeline. Please try again in a few minutes.';
        setModalError(message);
      } else {
        setShowModal(null);
        setFocusedPipeline(null);
        setModalError(null);
      }
    }).catch((e) => {
      // setActionLoading(false);
      if (e?.msg) {
        setModalError(e.msg);
      } else {
        setModalError('An unexpected error occurred while trying to undeploy the selected pipeline. Please try again in a few minutes.')
      }
    })
  }

  // isActive - the value to set the pipeline to
  const handleDeployOrUndeploy = (pipeline, workspaceId, toBeActive) => {
    setModalError(null);
    if (toBeActive) {
      handleDeploy(pipeline);
    } else {
      handleUndeploy(pipeline);
    }
  }

  const onShowDeploymentModal = (row) => {
    setShowModal(PIPELINE_ADMIN_MODAL.DEPLOY_OR_UNDEPLOY)
    setFocusedPipeline(row);
    setModalError(null);
  }

  // TODO - Make sure Version History for selected pipeline is available to the History Modal component
  const onShowPipelineHistory = (row) => {
    setShowModal(PIPELINE_ADMIN_MODAL.PIPELINE_HISTORY)
    setFocusedPipeline(row);
    setModalError(null);
  }

  const onShowPipelineRemovalModal = (row) => {
    setShowModal(PIPELINE_ADMIN_MODAL.DELETE_PIPELINE)
    setFocusedPipeline(row);
    setModalError(null);
  }

  const handleVersionSelection = (pipelineId, workspaceId, versions) => {
    setShowModal(PIPELINE_ADMIN_MODAL.DELETE_SELECTED_VERSIONS);
    setFocusedPipeline({
      pipelineId,
      workspaceId,
      versions,
    });
    setModalError(null);
  }

  const onModalToggle = (row) => {
    if (!row) {
      setShowModal(null);
      setFocusedPipeline(null);
      setModalError(null);
    }
  }

  // const onModalClose = () => {
  //   setShowModal(null);
  //   setFocusedPipeline(null);
  //   setModalError(null);
  // }

  const onFilterConfirmed = (filter) => {
    setFilterObj(filter);
    setShowModal(null);
    setModalError(null);
  }

  const onFilterClear = () => {
    setFilterObj({
      workspaces: [],
      status: 'all',
    })
    setShowModal(null);
    setModalError(null);
  }

  const handlePipelineClick = useCallback(({ original }) => {
    // Product behavior: Should change to the pipeline's workspace. https://wallarooai.slack.com/archives/D04D8SN29FW/p1716477004678179
    setCurrentWorkspaceId(original?.workspace?.workspace_id);
    const route = `workspaces/${original?.workspace?.workspace_id}/pipelines/${original?.name}/versions/${original?.last_version}`;
    window.open(`${window.location.origin}/${route}`, '_blank')
  }, [setCurrentWorkspaceId])

  const formatResultsCount = useCallback(val => `${val}/${totalPipelineCount} results`, [totalPipelineCount]);

  const errorText = error ? 'An unexpected error occurred while trying to load the pipeline list.' : 'No pipelines found.';

  // Hides the CTA section if there are simply no users to display
  const errorProps = error ? {} : { CTAInfo: null }

  return (
    <div>
      <div className="flex items-center justify-start mb-2">
        <SearchInput className="mr-2" value={searchQuery} onChange={handleSearchInputChange} block />
        <FilterCountButton activeFilters={countActivePipelineFilters(filterObj)} onClick={() => setShowModal(PIPELINE_ADMIN_MODAL.FILTER_PIPELINES)}>Filter</FilterCountButton>
      </div>
      <div className="relative">
        {
          (loading) && (
            <LoadingSpinner
              animationKey="pipeline-admin-table-loading-spinner"
              loadingText="Loading..."
              useOverlay
            />
          )
        }
        <div className={classNames('border shadow', {
          'hidden': !pipelines?.length || error,
        })}>
          <PipelineAdminTable
            pipelines={pipelines}
            onPipelineClick={handlePipelineClick}
            removePipeline={onShowPipelineRemovalModal}
            viewPipelineHistory={onShowPipelineHistory}
            showPipelineDeploymentModal={onShowDeploymentModal}
            selectedPage={selectedPage}
            resultsPerPage={resultsPerPage}
          />
        </div>
        {
          (!pipelines?.length || error) && (
            <CardView className="p-4" disableFullHeight>
              <ErrorSlate
                errorText={errorText}
                subText={null}
                Graphic={error ? BugGraphic : NoPipelinesGraphic}
                {...errorProps}
              />
            </CardView>
          )
        }
      </div>
      <PaginationRow
        className="p-4"
        numberOfResults={pipelines?.length}
        selectedPage={selectedPage}
        resultsPerPage={resultsPerPage}
        setSelectedPage={setSelectedPage}
        setResultsPerPage={setResultsPerPage}
        formatResultsCount={formatResultsCount}
      />
      {
        showModal === PIPELINE_ADMIN_MODAL.FILTER_PIPELINES && (
          <PipelineTableFilterModal
            filter={filterObj}
            workspaces={workspaces}
            showDialog={showModal === PIPELINE_ADMIN_MODAL.FILTER_PIPELINES}
            onDialogToggle={onModalToggle}
            onFilterConfirm={onFilterConfirmed}
            onClear={onFilterClear}
          />
        )
      }
      {
        showModal === PIPELINE_ADMIN_MODAL.DEPLOY_OR_UNDEPLOY && (
          <PipelineDeploymentModal
            pipeline={focusedPipeline?.original}
            error={modalError}
            showDialog={showModal === PIPELINE_ADMIN_MODAL.DEPLOY_OR_UNDEPLOY}
            onDialogToggle={onModalToggle}
            onPipelineUpdated={handleDeployOrUndeploy}
          />
        )
      }
      {
        showModal === PIPELINE_ADMIN_MODAL.DELETE_PIPELINE && (
          <AdminPipelineRemovalModal
            error={modalError}
            pipeline={focusedPipeline}
            showDialog={showModal === PIPELINE_ADMIN_MODAL.DELETE_PIPELINE}
            onPipelineRemoved={handlePipelineRemoval}
            onDialogToggle={onModalToggle}
          />
        )
      }
      {
        showModal === PIPELINE_ADMIN_MODAL.DELETE_SELECTED_VERSIONS && (
          <AdminPipelineVersionRemovalModal
            error={modalError}
            pipeline={focusedPipeline}
            showDialog={showModal === PIPELINE_ADMIN_MODAL.DELETE_SELECTED_VERSIONS}
            onPipelineVersionsRemoved={handleMultiVersionRemoval}
            onDialogToggle={onModalToggle}
          />
        )
      }
      {
        showModal === PIPELINE_ADMIN_MODAL.PIPELINE_HISTORY && (
          <AdminPipelineHistoryModal
            error={modalError}
            row={focusedPipeline}
            showDialog={showModal === PIPELINE_ADMIN_MODAL.PIPELINE_HISTORY}
            onDialogToggle={onModalToggle}
            onVersionsSelected={handleVersionSelection}
          />
        )
      }
    </div>
  )
}

const handleSearch = (pipelines, searchQuery, filterObj) => pipelines?.filter((pipeline) => {
  const workspace_filter = filterObj.workspaces.length === 0 || filterObj.workspaces.some(w => w.id === pipeline?.workspace?.id)

  const active = pipeline.deployment?.deployed ?? false;

  let status_filter = true;
  // Filters based on the status
  if (filterObj.status.toLowerCase() === STATUS.Inactive.toLowerCase()) {
    status_filter = !active;
  } else if (filterObj.status.toLowerCase() === STATUS.Active.toLowerCase()) {
    status_filter = active;
  }

  const searchTerm = searchQuery.toLowerCase();

  const pipelineName = pipeline?.pipeline?.name?.toLowerCase() || '';
  const workspaceName = pipeline?.workspace?.name?.toLowerCase() || '';
  const workspaceOwner = pipeline?.workspace?.created_by?.toLowerCase() || '';
  const updatedBy = pipeline?.pipeline_version?.updated_by?.toLowerCase() || '';

  const search_filter = pipelineName.includes(searchTerm)
    || workspaceName.includes(searchTerm)
    || workspaceOwner.includes(searchTerm)
    || updatedBy.includes(searchTerm);

  return workspace_filter && status_filter && search_filter;
})

const ContextualPipelineAdminTab = () => {
  const [filterObj, setFilterObj] = useState({
    workspaces: [],
    status: STATUS.All,
  })

  const [searchQuery, setSearchQuery] = useState('');

  const {
    data: pipelines,
    isLoading,
    error,
  } = useListPipelines();

  const {
    workspaces,
  } = useWorkspaceContext();

  const filteredPipelines = useMemo(() => handleSearch(pipelines, searchQuery, filterObj), [filterObj, searchQuery, pipelines]);

  const totalPipelineCount = pipelines?.length || 0;
  return (
    <PipelineAdminTab
      error={error}
      loading={isLoading}
      pipelines={filteredPipelines}
      workspaces={workspaces}
      totalPipelineCount={totalPipelineCount}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      filterObj={filterObj}
      setFilterObj={setFilterObj}
    />
  )
}

export default ContextualPipelineAdminTab;
